/**
 * The student Learning Zone sidebar (SIDEBAR_TREE) and its row view ids.
 * The sample scenes / songs / stories below are no longer shown to students (those rows show the
 * published course content in their own screen's look, or say none has been added yet); the
 * admin / coach lesson plan pages still list them (lessonPlanColumns.js, lessonPlanProposal.js).
 */

// `syllabusTopic` ties a row (and the rows under it) to a coach Syllabus Tracker topic: the course
// content admins add there is numbered like that topic's sessions ("Action Song 3").
export const SIDEBAR_TREE = [
  { id: 'wtf', label: 'WTF', icon: '😊', view: 'wtf' },
  {
    id: 'english',
    label: 'English',
    icon: 'Aa',
    defaultOpen: true,
    view: 'scenes',
    children: [
      {
        id: 'action-songs',
        label: 'Action Songs',
        icon: '🎵',
        view: 'action-songs',
        syllabusTopic: 'Action Songs',
        children: [
          { id: 'vocal-songs', label: 'Vocal Songs', icon: '🎤', view: 'action-songs-vocal' },
          { id: 'instrumental-songs', label: 'Instrumental Songs', icon: '🎹', view: 'action-songs-instrumental' },
        ],
      },
      {
        // Scenes comes in two levels. They have no screen of their own; once a published
        // course is placed in one (admin Courses), it shows that course's content.
        id: 'scenes',
        label: 'Scenes',
        icon: '🎬',
        view: 'scenes',
        syllabusTopic: 'Scenes',
        children: [
          { id: 'beginners', label: 'Beginners', icon: '🧑' },
          { id: 'intermediate', label: 'Intermediate', icon: '📊' },
        ],
      },
    ],
  },
  {
    // Opens GCompris straight away; its sibling apps have no screens yet ("Coming soon!"),
    // and Courses keeps the coach-assigned Computer Apps lessons/quizzes one click away.
    id: 'computer-apps',
    label: 'Computer Apps',
    icon: '💻',
    view: 'gcompris',
    syllabusTopic: 'Computer apps',
    children: [
      { id: 'gcompris', label: 'GCompris', icon: '🎮', view: 'gcompris' },
      {
        // The art lessons (Art Therapy's rows until 2026-10-26). Opens Beginners straight away;
        // Courses keeps the coach-assigned Art course (workshops, quizzes, competitions) one click away.
        id: 'artweaver',
        label: 'Artweaver',
        icon: '🖌️',
        view: 'art-beginners',
        children: [
          { id: 'art-beginners', label: 'Beginners', icon: '🖍️', view: 'art-beginners', compact: true },
          { id: 'art-intermediate', label: 'Intermediate', icon: '🖌️', view: 'art-intermediate', compact: true },
          { id: 'art-courses', label: 'Courses', icon: '📚', to: '/student/art', compact: true },
        ],
      },
      { id: 'typing-master', label: 'Typing Master', icon: '⌨️' },
      { id: 'canva', label: 'Canva', icon: '🖼️' },
      { id: 'toonytool', label: 'Toonytool', icon: '🎞️' },
      { id: 'computer-courses', label: 'Courses', icon: '📚', to: '/student/computer-apps' },
    ],
  },
  { id: 'life-skills', label: 'Life Skills', icon: '💡', to: '/student/life-skills' },
  {
    // Opens Balgopal Stories straight away.
    id: 'stories',
    label: 'Stories',
    icon: '📖',
    view: 'stories-balgopal',
    // compact: small rows so "2 min Balgruha Stories" fits the 256px sidebar.
    children: [
      { id: 'balgopal-stories', label: 'Balgopal Stories', icon: '👑', view: 'stories-balgopal', compact: true },
      { id: 'saint-stories', label: 'Saint Stories', icon: '👑', view: 'stories-saint', compact: true, syllabusTopic: 'Saint Story' },
      {
        id: 'balgruha-stories',
        label: '2 min Balgruha Stories',
        icon: '👑',
        view: 'stories-balgruha',
        compact: true,
        syllabusTopic: '2 Min Balgruha Stories',
      },
    ],
  },
  {
    // Course category "Art" (named "Art Therapy" until 2026-10-26, when its rows moved to Computer
    // Apps > Artweaver). It opens the content admins place right in it, like a category they added
    // (courseSubcategoryRows.js); its task type rows and added subcategories go under it.
    id: 'art-therapy',
    label: 'Fun with Art',
    icon: '🎨',
  },
  { id: 'medical', label: 'Medical', icon: '➕', view: 'medical' },
  { id: 'homework', label: 'Homework', icon: '📋', to: '/student/homework' },
  { id: 'diary', label: 'Diary', icon: '📔', view: 'diary' },
];

export const SCENES = [
  {
    id: 1,
    title: 'Scene 1',
    coins: 30,
    intro: "Let's learn how to greet and talk with your coach in English!",
  },
  { id: 2, title: 'Scene 2', coins: 20, intro: 'Practice this scene with your coach.' },
  { id: 3, title: 'Scene 3', coins: 25, intro: 'Practice this scene with your coach.' },
  { id: 4, title: 'Scene 4', coins: 20, intro: 'Practice this scene with your coach.' },
  { id: 5, title: 'Scene 5', coins: 30, intro: 'Practice this scene with your coach.' },
  { id: 6, title: 'Scene 6', coins: 25, intro: 'Practice this scene with your coach.' },
  { id: 7, title: 'Scene 7', coins: 20, intro: 'Practice this scene with your coach.' },
];

// Sidebar view ids of the Action Songs rows (courseSubcategoryRows.js swaps each for its content
// row, which keeps the Action Songs screen, ActionSongsView). `type` filters the sample song list
// (null = every song); `title` is the row's heading.
export const ACTION_SONG_VIEWS = {
  'action-songs': { title: 'Action Songs', type: null },
  'action-songs-vocal': { title: 'Vocal Songs', type: 'vocal' },
  'action-songs-instrumental': { title: 'Instrumental Songs', type: 'instrumental' },
};

const VOCAL_INTRO = "Let's sing this action song together. Sing along, follow the actions and have fun!";
const INSTRUMENTAL_INTRO = "Let's enjoy this instrumental action song. Listen, follow the actions and have fun!";

export const ACTION_SONGS = [
  { id: 1, title: 'Roly Poly', emoji: '🤸', type: 'instrumental', coins: 30, intro: INSTRUMENTAL_INTRO },
  { id: 2, title: 'God made the blue sky', emoji: '🌤️', type: 'vocal', coins: 20, intro: VOCAL_INTRO },
  { id: 3, title: 'When you are happy', emoji: '🙌', type: 'vocal', coins: 25, intro: VOCAL_INTRO },
  { id: 4, title: 'Ten Tiny Fingers', emoji: '🖐️', type: 'vocal', coins: 20, intro: VOCAL_INTRO },
  { id: 5, title: 'Hop a little', emoji: '🐰', type: 'instrumental', coins: 30, intro: INSTRUMENTAL_INTRO },
  { id: 6, title: 'Dog says Bow', emoji: '🐶', type: 'instrumental', coins: 25, intro: INSTRUMENTAL_INTRO },
];

export const BALGOPAL_STORIES = [
  { id: 1, title: 'The birth of Baalgopal', emoji: '👶', coins: 30 },
  { id: 2, title: 'The ladoo of love', emoji: '🍡', coins: 20 },
  { id: 3, title: "Yashoda's glimpse of the Universe", emoji: '🌌', coins: 25 },
  { id: 4, title: 'The lost calf', emoji: '🐄', coins: 20 },
  { id: 5, title: 'The butter thief', emoji: '🧈', coins: 30 },
  { id: 6, title: "Gopal's garland", emoji: '🌸', coins: 25 },
  { id: 7, title: "Nand baba's Night of worry", emoji: '🌙', coins: 20 },
  { id: 8, title: "Gopal's Birthday celebration", emoji: '🎂', coins: 30 },
];

export const SAINT_STORIES = [
  { id: 1, title: 'Saint Dnyaneshwar & Changdev', emoji: '👴', coins: 30 },
  { id: 2, title: 'Saint Muktabai', emoji: '🙏', coins: 20 },
  { id: 3, title: 'Saint Janaabai', emoji: '🪔', coins: 25 },
  { id: 4, title: 'Saint Bhakt Pundalik', emoji: '🧱', coins: 20 },
  { id: 5, title: 'Saint Saavta Mali', emoji: '🌿', coins: 30 },
  { id: 6, title: 'Saint Chokhoba', emoji: '🛕', coins: 25 },
  { id: 7, title: 'संत ज्ञानेश्वर - एक ही परमात्मा', emoji: '🕉️', coins: 20 },
];

// Coins are only placeholders past the first story; the design only shows 30.
export const BALGRUHA_STORIES = [
  {
    id: 1,
    title: 'Dogs are loving and Loyal',
    emoji: '🐶',
    coins: 30,
    intro: 'Watch this short story and learn about love, care and friendship.',
  },
  {
    id: 2,
    title: 'WHO TOOK MY SHARPNER',
    emoji: '✏️',
    coins: 20,
    intro: 'Watch this short story and learn about honesty and telling the truth.',
  },
  {
    id: 3,
    title: 'Sharing is Caring',
    emoji: '🤝',
    coins: 25,
    intro: 'Watch this short story and learn about sharing and caring for others.',
  },
  {
    id: 4,
    title: 'A New Friend',
    emoji: '👫',
    coins: 20,
    intro: 'Watch this short story and learn about being kind and making new friends.',
  },
  {
    id: 5,
    title: 'My Dream',
    emoji: '💭',
    coins: 30,
    intro: 'Watch this short story and learn about big dreams and working towards them.',
  },
  {
    id: 6,
    title: 'Helping Each Other',
    emoji: '🤗',
    coins: 25,
    intro: 'Watch this short story and learn about helping others and working together.',
  },
];

// Sidebar view ids of the Stories rows. courseSubcategoryRows.js swaps each for its content row,
// which keeps the Stories screen (StoriesView) with the options below; `stories` is the sample list
// (lesson plan pages only). `title` / `description` / `icon` describe the list. Every option is
// optional:
//   numberedHeadings    - the selected story reads "Story 6 - Title" / "Story 6
//                         Worksheet" (unless its title has a number of its own);
//                         otherwise its own title is used
//                         ("Saint Muktabai" / "Saint Muktabai Worksheet")
//   standaloneHeader    - the list's title and description become the page
//                         heading, instead of a "Stories" heading over a list card
//   hideTileCoins       - leave the coins off the strip tiles (the selected
//                         story's panel still shows them)
//   practiceDescription - the sample screen's Your Practice subtitle (the content
//                         rows always say "...for this story...")
// A story may also carry an `intro`, shown under its title.
// `syllabusTopic` (when set) ties a story list to the coach Syllabus
// Tracker's Topic of the same name, so PracticePanel's Upload reaches that
// child's row there. Balgopal Stories has no matching Syllabus Tracker
// topic, so it's left out - Upload stays local-only for it, unchanged.
export const STORY_VIEWS = {
  'stories-balgopal': {
    title: 'Balgopal Stories',
    description: 'Beautiful stories of little Balgopal and his divine pastimes.',
    icon: '👑',
    numberedHeadings: true,
    stories: BALGOPAL_STORIES,
  },
  'stories-saint': {
    title: 'Saint Stories',
    description: 'Inspiring stories of great saints and their values.',
    icon: '🧘',
    practiceDescription: 'Record yourself for this story and upload it.',
    syllabusTopic: 'Saint Story',
    stories: SAINT_STORIES,
  },
  'stories-balgruha': {
    title: '2 min Balgruha Stories',
    description: 'Short stories from our Balgruha. Real moments. Real learning!',
    standaloneHeader: true,
    hideTileCoins: true,
    practiceDescription: 'Record yourself for this story and upload it.',
    syllabusTopic: '2 Min Balgruha Stories',
    stories: BALGRUHA_STORIES,
  },
};

// Sidebar view ids of the art rows (Computer Apps > Artweaver). courseSubcategoryRows.js swaps each
// for its content row, which keeps the art screen (ArtTherapyView): a numbered list of the art
// lessons admins placed there, each opening its own drawing page. `title` / `description` head the list.
export const ART_VIEWS = {
  'art-beginners': {
    title: 'Artweaver - Beginners',
    description: "Let's draw, colour and explore our creativity!",
  },
  'art-intermediate': {
    title: 'Artweaver - Intermediate',
    description: "Let's add shapes, shading and detail to our creativity!",
  },
};
